import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Hook personalizado para sincronizar parámetros de URL con el estado React
 * Permite bookmarking y compartir URLs con filtros preseleccionados
 * 
 * @param {Object} paramsMapping - Mapeo de parámetros URL a estados React
 * @returns {void}
 */
export const useUrlParams = (paramsMapping = {}) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Efecto para leer parámetros de URL y actualizar estados
  useEffect(() => {
    // Obtener query string como objeto
    const searchParams = new URLSearchParams(location.search);
    const params = Object.fromEntries(searchParams.entries());
    
    // Actualizar estados según parámetros encontrados
    Object.keys(paramsMapping).forEach(paramKey => {
      const { [paramKey]: value, ['set' + capitalizeFirst(paramKey)]: setter } = paramsMapping;
      
      if (params[paramKey] && setter) {
        // Convertir valores según el tipo de dato actual
        const typedValue = convertType(params[paramKey], value);
        
        // Solo actualizar si es diferente al valor actual
        if (typedValue !== value) {
          setter(typedValue);
        }
      }
    });
  }, [location.search]);
  
  // Efecto para actualizar URL cuando cambian los estados
  useEffect(() => {
    // Crear objeto con parámetros a incluir en URL
    const urlParams = {};
    
    Object.keys(paramsMapping).forEach(paramKey => {
      const value = paramsMapping[paramKey];
      
      // Solo incluir valores definidos y no vacíos
      if (value !== undefined && value !== null && value !== '' && value !== 'all') {
        urlParams[paramKey] = String(value);
      }
    });
    
    // Construir query string
    const searchParams = new URLSearchParams();
    Object.entries(urlParams).forEach(([key, value]) => {
      searchParams.set(key, value);
    });
    
    const queryString = searchParams.toString();
    const newUrl = `${location.pathname}${queryString ? '?' + queryString : ''}`;
    
    // Actualizar URL si ha cambiado
    if (location.search !== `?${queryString}` && queryString !== '') {
      navigate(newUrl, { replace: true });
    } else if (location.search && queryString === '') {
      navigate(location.pathname, { replace: true });
    }
  }, [Object.values(paramsMapping).filter(v => typeof v !== 'function')]);
};

/**
 * Capitaliza la primera letra de un string
 * @param {string} str - String a capitalizar
 * @returns {string} String capitalizado
 */
const capitalizeFirst = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Convierte un valor de string a su tipo correspondiente
 * @param {string} value - Valor a convertir
 * @param {any} template - Valor original como plantilla para determinar tipo
 * @returns {any} Valor convertido al tipo adecuado
 */
const convertType = (value, template) => {
  if (template === undefined || template === null) {
    return value; // Si no hay plantilla, devolver el valor como string
  }
  
  const type = typeof template;
  
  switch (type) {
    case 'number':
      return Number(value);
    case 'boolean':
      return value === 'true';
    default:
      return value;
  }
};
